/**
 * RunDetailView — full drill-down for a Creative Review garden run.
 *
 * Route: /admin/creative-review/:clientUid/runs/:runId
 * Tabs: Overview, Creatives, Reasoning, Concepts, Persona Routing, Strategy,
 *       Errors, Execution Log
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  LayoutGrid,
  List,
  ChevronRight,
  X,
  Hammer,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Network,
  Fingerprint,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  gardenApi,
  graphBuildApi,
  adCatalogApi,
  type ConceptEmission,
  type CreativeReviewPipelineState,
  type CreativeReviewRunArtifact,
  type CreativeFootprintResponse,
  type GardenRun,
  type GardenRunTask,
  type GraphBuildRun,
  type PersonaCandidateScore,
  type RoutingHealthResponse,
  type ReviewedPair,
} from '@/services/api';
import {
  subscribeToGardenRun,
  subscribeToGardenRunPipelines,
} from '@/services/firestoreListeners';
import { getOrganizationId } from '@/services/firebase';
import { RunStatusBadge } from '@/components/pdo/StatusBadges';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useFetch, timeAgo } from '@/components/pdo/utils';
import { PipelineOverviewStrip } from './pipeline/PipelineOverviewStrip';
import { PipelineDetailModal } from './pipeline/PipelineDetailModal';
import { ExecutionLogTab } from './pipeline/ExecutionLogTab';
import type { AttentionAtlasViewProps } from '@neurograph/skill-apps/attention-atlas';
// Lazy-load the heavy atlas viewer so main bundle stays lean
import { lazy, Suspense } from 'react';
const AttentionAtlasView = lazy(() =>
  import('@neurograph/skill-apps/attention-atlas').then((m) => ({ default: m.AttentionAtlasView }))
);

const TABS = [
  'overview',
  'creatives',
  'tasks',
  'reasoning',
  'concepts',
  'routing',
  'strategy',
  'errors',
  'execution_log',
] as const;
type Tab = (typeof TABS)[number];

const RUNNING = new Set(['running', 'queued', 'pending']);
const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useGardenRunLive(clientUid: string, runId: string) {
  const [run, setRun] = useState<GardenRun | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRun = useCallback(async () => {
    try {
      const data = await gardenApi.getRun(clientUid, runId);
      setRun(data);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [clientUid, runId]);

  useEffect(() => {
    fetchRun();
    let mounted = true;
    const unsub = subscribeToGardenRun(
      runId,
      (liveRun) => { if (!mounted) return; if (liveRun) { setRun(liveRun); setLoading(false); } },
      (err) => { if (!mounted) return; console.warn('[RunDetailView] Firestore error:', err); },
    );
    return () => { mounted = false; unsub(); };
  }, [runId, fetchRun]);

  return { run, error, loading, refetch: fetchRun };
}

function useGardenRunPipelinesLive(clientUid: string, runId: string, isRunning: boolean) {
  const [pipelines, setPipelines] = useState<CreativeReviewPipelineState[]>([]);
  const fetchPipelines = useCallback(async () => {
    try {
      const data = await gardenApi.listRunPipelines(clientUid, runId);
      setPipelines(data.pipelines ?? []);
    } catch { /* non-fatal */ }
  }, [clientUid, runId]);

  useEffect(() => {
    fetchPipelines();
    let mounted = true;
    const unsub = subscribeToGardenRunPipelines(
      runId,
      (live) => { if (!mounted) return; setPipelines(live); },
      (err) => { console.warn('[RunDetailView] Firestore pipelines error:', err); },
    );
    return () => { mounted = false; unsub(); };
  }, [runId, fetchPipelines]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!DEV_BYPASS || !isRunning) { if (pollRef.current) clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(fetchPipelines, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isRunning, fetchPipelines]);

  return pipelines;
}

// ---------------------------------------------------------------------------
// Shared UI primitives
// ---------------------------------------------------------------------------

function TabButton({ active, label, count, onClick }: {
  active: boolean; label: string; count?: number; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
        active ? 'border-brand-teal text-brand-teal' : 'border-transparent text-slate-500 hover:text-slate-800',
      )}
    >
      {label}
      {count !== undefined && <span className="ml-1.5 text-xs text-slate-400">({count})</span>}
    </button>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function VerdictChip({ verdict }: { verdict?: string | null }) {
  if (!verdict) return <span className="text-xs text-slate-300">—</span>;
  const colors: Record<string, string> = {
    strong_fit: 'bg-green-50 text-green-700 border-green-200',
    moderate_fit: 'bg-blue-50 text-blue-700 border-blue-200',
    weak_fit: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    poor_fit: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <span className={clsx('text-xs px-2 py-0.5 rounded-full border font-medium', colors[verdict] ?? 'bg-slate-50 text-slate-500 border-slate-200')}>
      {verdict.replace(/_/g, ' ')}
    </span>
  );
}

function StageBadge({ stage }: { stage?: string }) {
  const map: Record<string, string> = {
    raw_extracted: 'bg-slate-100 text-slate-600',
    resolved: 'bg-blue-50 text-blue-700',
    canonical_pdo: 'bg-emerald-50 text-emerald-700',
  };
  const label: Record<string, string> = {
    raw_extracted: 'Raw',
    resolved: 'Resolved',
    canonical_pdo: 'PDO',
  };
  const s = stage ?? 'raw_extracted';
  return (
    <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide', map[s] ?? 'bg-slate-100 text-slate-600')}>
      {label[s] ?? s}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Graph Build Banner (D4)
// ---------------------------------------------------------------------------

function GraphBuildBanner({ clientUid, triggeredByRunId }: { clientUid: string; triggeredByRunId?: string }) {
  const navigate = useNavigate();
  const [builds, setBuilds] = useState<GraphBuildRun[]>([]);
  const [triggering, setTriggering] = useState(false);
  const [latestBuild, setLatestBuild] = useState<GraphBuildRun | null>(null);
  const [showModal, setShowModal] = useState(false);

  const loadBuilds = useCallback(async () => {
    try {
      const resp = await graphBuildApi.list(clientUid, 5);
      setBuilds(resp.builds ?? []);
      setLatestBuild(resp.builds?.[0] ?? null);
    } catch { /* non-fatal */ }
  }, [clientUid]);

  useEffect(() => { loadBuilds(); }, [loadBuilds]);

  // Poll every 5s when a build is active
  useEffect(() => {
    if (!latestBuild || !['running', 'queued'].includes(latestBuild.status)) return;
    const iv = setInterval(loadBuilds, 5000);
    return () => clearInterval(iv);
  }, [latestBuild, loadBuilds]);

  const triggerBuild = async (env: string) => {
    setTriggering(true);
    try {
      const resp = await graphBuildApi.trigger(clientUid, {
        env,
        triggered_by_run_id: triggeredByRunId,
      });
      await loadBuilds();
      navigate(`/admin/creative-review/${clientUid}/graph-builds/${resp.build_id}`);
    } catch (e) {
      alert(`Build trigger failed: ${String(e)}`);
    } finally {
      setTriggering(false);
      setShowModal(false);
    }
  };

  const statusIcon = (s: string) => {
    if (s === 'running' || s === 'queued') return <Loader2 className="w-3 h-3 animate-spin text-blue-500" />;
    if (s === 'completed') return <CheckCircle2 className="w-3 h-3 text-green-500" />;
    if (s === 'failed') return <XCircle className="w-3 h-3 text-red-500" />;
    return <Clock className="w-3 h-3 text-slate-400" />;
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <Hammer className="w-4 h-4 text-slate-500 shrink-0" />
        <div>
          <div className="text-xs font-semibold text-slate-700">Graph Build</div>
          {latestBuild ? (
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
              {statusIcon(latestBuild.status)}
              <span>{latestBuild.status}</span>
              <span className="text-slate-300">·</span>
              <Link
                to={`/admin/creative-review/${clientUid}/graph-builds/${latestBuild.build_id}`}
                className="text-brand-teal underline"
              >
                {latestBuild.build_id.slice(0, 14)}…
              </Link>
            </div>
          ) : (
            <div className="text-xs text-slate-400 mt-0.5">No builds yet for this client</div>
          )}
        </div>
      </div>
      <button
        onClick={() => setShowModal(true)}
        disabled={triggering}
        className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium text-slate-700 disabled:opacity-50"
      >
        {triggering ? 'Starting…' : 'Build graph'}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="font-semibold text-slate-900">Trigger graph build</h2>
            <p className="text-sm text-slate-600">
              This will run <code className="text-xs font-mono bg-slate-100 px-1 rounded">./graph.sh build --client-uid {clientUid}</code>
              as a subprocess in the FastAPI process.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowModal(false)} className="text-sm px-4 py-2 rounded-lg border text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={() => triggerBuild('staging')} className="text-sm px-4 py-2 rounded-lg bg-brand-teal text-white hover:opacity-90">
                Staging
              </button>
              <button onClick={() => triggerBuild('production')} className="text-sm px-4 py-2 rounded-lg bg-slate-800 text-white hover:opacity-90">
                Production
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// C1 — Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({ run, artifact, clientUid }: { run: GardenRun; artifact: CreativeReviewRunArtifact | null; clientUid: string }) {
  const backfill = (run.stage_outcomes?.creative_review_backfill ?? {}) as Record<string, unknown>;
  const summary = (backfill.summary ?? {}) as Record<string, unknown>;

  const rawCount = artifact?.concepts_emitted?.filter((c) => c.stage === 'raw_extracted' || !c.stage).length ?? 0;
  const resolvedCount = artifact?.concepts_emitted?.filter((c) => c.stage === 'resolved').length ?? 0;
  const pdoCount = artifact?.concepts_emitted?.filter((c) => c.stage === 'canonical_pdo').length ?? 0;

  const missingSteps = (summary.missing_steps as string[] | undefined) ?? [];
  const cliCommands = (summary.cli_commands as string[] | undefined) ?? [];

  // Build narrative from stage outcomes
  const narrative: string[] = [];
  const so = run.stage_outcomes ?? {};
  if (so.creative_review_backfill) {
    const bf = (so.creative_review_backfill as Record<string, unknown>);
    const bfs = (bf.summary ?? {}) as Record<string, unknown>;
    narrative.push(
      `Selected ${bfs.total ?? '?'} creative–persona pairs for review (${bfs.success ?? '?'} succeeded, ${bfs.failed ?? '?'} failed, ${bfs.skipped ?? '?'} skipped).`,
    );
    if (bfs.elapsed_s) narrative.push(`Backfill completed in ${bfs.elapsed_s}s.`);
    if (bfs.run_artifact_id) narrative.push(`Run artifact saved as ${bfs.run_artifact_id}.`);
    if (bfs.deployed_creatives_count) narrative.push(`Corpus: ${bfs.deployed_creatives_count} deployed creatives.`);
  }
  if (so.semantic_clustering) {
    const sc = ((so.semantic_clustering as Record<string, unknown>).summary ?? {}) as Record<string, unknown>;
    if (sc.cluster_count) narrative.push(`Semantic clustering produced ${sc.cluster_count} strategy clusters.`);
  }
  if (so.brief_synthesis) {
    const bs = ((so.brief_synthesis as Record<string, unknown>).summary ?? {}) as Record<string, unknown>;
    if (bs.brief_count) narrative.push(`Brief synthesis generated ${bs.brief_count} strategic briefs.`);
  }

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi label="Pairs" value={String(summary.total ?? artifact?.pairs?.length ?? '—')} />
        <Kpi label="Success" value={String(summary.success ?? '—')} />
        <Kpi label="Failed" value={String(summary.failed ?? '—')} />
        <Kpi label="Concepts" value={rawCount + resolvedCount + pdoCount} sub={`${rawCount} raw`} />
        <Kpi label="Resolved" value={resolvedCount} sub="stage B" />
        <Kpi label="PDO" value={pdoCount} sub="stage C" />
        <Kpi label="Personas" value={artifact?.persona_routing?.length ?? '—'} sub="creatives routed" />
      </div>

      {/* What happened */}
      {narrative.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
          <h3 className="text-xs font-semibold text-slate-600 mb-2">What happened</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {narrative.map((n, i) => (
              <li key={i} className="flex gap-2">
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preconditions panel */}
      {missingSteps.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
          <h3 className="text-xs font-semibold text-amber-700 mb-2">Missing prerequisites</h3>
          <ul className="space-y-1 text-xs text-amber-800">
            {missingSteps.map((s, i) => <li key={i}>• {s}</li>)}
          </ul>
          {cliCommands.length > 0 && (
            <div className="mt-3 space-y-1">
              {cliCommands.map((cmd, i) => (
                <code key={i} className="block bg-amber-100 px-2 py-1 rounded text-[11px] font-mono">{cmd}</code>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Run error */}
      {run.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{run.error}</div>
      )}

      {/* Stage outcomes */}
      <div>
        <h3 className="text-xs font-semibold text-slate-600 mb-2">Stage outcomes</h3>
        <div className="space-y-2">
          {Object.entries(run.stage_outcomes ?? {}).map(([stage, payload]) => {
            const p = payload as Record<string, unknown>;
            const s = (p.summary ?? {}) as Record<string, unknown>;
            return (
              <div key={stage} className="rounded-lg border border-slate-200 bg-white px-3 py-2 flex justify-between items-center gap-4">
                <span className="text-xs font-semibold text-slate-700">{stage}</span>
                <span className="text-xs text-slate-400 font-mono">
                  {Object.entries(s).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(' · ')}
                </span>
                <span className={clsx('text-xs px-2 py-0.5 rounded-full', p.status === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600')}>
                  {String(p.status ?? '—')}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Graph Build Banner */}
      <GraphBuildBanner clientUid={clientUid} triggeredByRunId={run.run_id} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 4B — AttentionAtlasSection (lazy-loaded)
// ---------------------------------------------------------------------------

function AttentionAtlasSection({ pair, clientUid }: { pair: ReviewedPair; clientUid: string }) {
  // Fetch the cortex data for this creative (all personas)
  const cortexQuery = useFetch(
    () =>
      pair.creative_id
        ? fetch(
            `/api/v1/creative-reviews/creatives/${clientUid}/${pair.creative_id}/cortex`
          )
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        : Promise.resolve(null),
    [clientUid, pair.creative_id],
  );

  const cortexData = cortexQuery.data as {
    cortex_by_persona?: Record<string, { cortex_state?: unknown }>;
  } | null;

  // Check if the pair has atlas data exposed via the run artifact
  const pairAny = pair as unknown as Record<string, unknown>;
  const atlasData = pairAny['attention_atlas'] as Record<string, unknown> | null | undefined;

  if (!atlasData && !cortexData?.cortex_by_persona) {
    return null;
  }

  const personaWeights: AttentionAtlasViewProps['personaWeights'] = [
    { persona_id: pair.persona_id, persona_name: pair.persona_name ?? null, weight: 1.0 },
  ];

  const toCortexByPersona = (
    raw: Record<string, { cortex_state?: unknown }> | undefined
  ): AttentionAtlasViewProps['cortexByPersona'] =>
    Object.fromEntries(
      Object.entries(raw ?? {}).map(([pid, entry]) => [
        pid,
        (entry as Record<string, unknown>)['cortex_state'] as unknown,
      ])
    ) as AttentionAtlasViewProps['cortexByPersona'];

  const cortexByPersona = toCortexByPersona(cortexData?.cortex_by_persona);

  if (!atlasData) {
    return (
      <div>
        <h3 className="text-xs font-semibold text-slate-600 mb-2">Cognitive Trace</h3>
        <Suspense fallback={<div className="text-xs text-slate-400">Loading cortex…</div>}>
          <AttentionAtlasView
            atlas={{} as AttentionAtlasViewProps['atlas']}
            imageUrl={pair.image_url ?? ''}
            variant="cortex-trace"
            cortexByPersona={cortexByPersona}
            personaWeights={personaWeights}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-600 mb-2">
        Attention Atlas
        {Object.keys(cortexByPersona ?? {}).length > 0 && (
          <span className="ml-1 text-[10px] font-normal text-indigo-500">+ Cortex</span>
        )}
      </h3>
      <Suspense fallback={<div className="text-xs text-slate-400">Loading atlas…</div>}>
        <AttentionAtlasView
          atlas={atlasData as unknown as AttentionAtlasViewProps['atlas']}
          imageUrl={pair.image_url ?? ''}
          variant="stacked"
          cortexByPersona={cortexByPersona}
          personaWeights={personaWeights}
        />
      </Suspense>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C2b — CreativePairPanel slide-over
// ---------------------------------------------------------------------------

function CreativePairPanel({ pair, clientUid, runId, onClose }: {
  pair: ReviewedPair;
  clientUid: string;
  runId: string;
  onClose: () => void;
}) {
  const conceptsQuery = useFetch(
    () => gardenApi.listCreativeConcepts(clientUid, runId, pair.creative_id),
    [clientUid, runId, pair.creative_id],
  );
  const concepts = conceptsQuery.data?.concepts ?? [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="relative z-50 w-full max-w-xl bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900 text-sm font-mono">{pair.creative_id}</h2>
            <p className="text-xs text-slate-500">{pair.persona_name || pair.persona_id.slice(-10)}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-5">
          {/* Observation pass notice */}
          {pair.verdict === 'observe' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">◑</span>
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Observation pass</span> — no verdict or scores yet.
                Run <code className="bg-amber-100 px-1 rounded">review_depth: full</code> for a graded analysis.
              </p>
            </div>
          )}

          {/* Scores — hidden for observation passes */}
          {pair.verdict !== 'observe' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="text-xs text-slate-500">Verdict</div>
                <VerdictChip verdict={pair.verdict} />
              </div>
              <div className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="text-xs text-slate-500">Score</div>
                <div className="font-semibold text-slate-800">{pair.overall_score ?? '—'}</div>
              </div>
              {pair.top_strength && (
                <div className="col-span-2 rounded-lg border border-green-100 bg-green-50 px-3 py-2">
                  <div className="text-xs text-green-600 font-semibold mb-1">Top Strength</div>
                  <p className="text-xs text-slate-700">{pair.top_strength}</p>
                </div>
              )}
              {pair.critical_gap && (
                <div className="col-span-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                  <div className="text-xs text-red-600 font-semibold mb-1">Critical Gap</div>
                  <p className="text-xs text-slate-700">{pair.critical_gap}</p>
                </div>
              )}
            </div>
          )}

          {/* Routing Rationale */}
          {pair.routing_rationale && (
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5 flex-wrap">
                <span>Persona Assignment Rationale</span>
                <span className="text-[10px] font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                  {pair.routing_rationale.pass_name ?? 'cold_start'}
                </span>
                {(() => {
                  const selectedScores = pair.routing_rationale.all_scores?.[pair.persona_id];
                  const om = selectedScores?.overlap_method;
                  const fs = selectedScores?.footprint_source;
                  return (
                    <>
                      {om && (
                        <span className={`text-[10px] font-normal px-1.5 py-0.5 rounded ${
                          om === 'cross_cosine'
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {om === 'cross_cosine' ? '⚡ cross-cosine' : 'jaccard'}
                        </span>
                      )}
                      {fs && fs !== 'unknown' && (
                        <span className={`text-[10px] font-normal px-1.5 py-0.5 rounded ${
                          fs === 'atlas'
                            ? 'bg-purple-100 text-purple-700 border border-purple-200'
                            : fs === 'extracted_concepts'
                              ? 'bg-teal-100 text-teal-700 border border-teal-200'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {fs === 'atlas' ? '🗺 atlas' : fs === 'extracted_concepts' ? '🔬 concepts' : fs}
                        </span>
                      )}
                    </>
                  );
                })()}
              </h3>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 space-y-2 text-xs">
                {pair.routing_rationale.creative_text_empty && (
                  <div className="flex items-start gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    <span className="mt-0.5">⚠</span>
                    <span>Creative had no extractable copy — routing used name/label only</span>
                  </div>
                )}
                {pair.routing_rationale.creative_text_preview && (
                  <div>
                    <span className="text-slate-500 font-medium">Creative text embedded:</span>
                    <p className="text-slate-600 mt-0.5 italic">{pair.routing_rationale.creative_text_preview}</p>
                  </div>
                )}
                {pair.routing_rationale.explanation && (
                  <p className="text-slate-500">{pair.routing_rationale.explanation}</p>
                )}
                {/* Score breakdown for all candidates */}
                {pair.routing_rationale.all_scores && (
                  <div>
                    {/* Determine if composite scores are available */}
                    {(() => {
                      const entries = Object.entries(pair.routing_rationale.all_scores);
                      const hasComposite = entries.some(([, s]) => s.composite != null);
                      return (
                        <>
                          {(() => {
                            const anyEntry = entries[0]?.[1];
                            const om = anyEntry?.overlap_method;
                            return (
                              <p className="text-slate-500 font-medium mb-1 flex items-center gap-1.5">
                                All candidate scores ({hasComposite ? 'composite' : 'cosine similarity'})
                                {hasComposite && om && (
                                  <span className={`text-[9px] px-1 py-0.5 rounded font-mono ${
                                    om === 'cross_cosine'
                                      ? 'bg-blue-50 text-blue-600 border border-blue-200'
                                      : 'bg-slate-100 text-slate-400'
                                  }`}>
                                    {om}
                                  </span>
                                )}
                              </p>
                            );
                          })()}
                          {hasComposite && (
                            <div className="grid grid-cols-5 gap-x-1 text-[10px] text-slate-400 font-medium px-2 pb-0.5 border-b border-slate-100 mb-1">
                              <span className="col-span-2">Persona</span>
                              <span className="text-right tabular-nums">Overlap</span>
                              <span className="text-right tabular-nums">Scenario</span>
                              <span className="text-right tabular-nums font-semibold text-slate-500">Score</span>
                            </div>
                          )}
                          <div className="space-y-0.5">
                            {entries.map(([pid, scores]) => {
                              const isSelected = pid === pair.persona_id;
                              const displayName = isSelected
                                ? (pair.persona_name || scores.persona_name || pid)
                                : (scores.persona_name || pid.slice(-12));
                              const mainScore = scores.composite ?? scores.blended_score ?? scores.embedding_cosine;
                              return (
                                <div key={pid} className={`flex items-center gap-1.5 rounded px-2 py-1 ${isSelected ? 'bg-indigo-50 border border-indigo-200' : 'bg-white border border-slate-100'}`}>
                                  <span className={`text-[10px] flex-1 min-w-0 truncate ${isSelected ? 'text-indigo-700 font-semibold' : 'text-slate-500'}`} title={pid}>
                                    {isSelected ? `✓ ${displayName}` : displayName}
                                  </span>
                                  {hasComposite ? (
                                    <>
                                      <span className={`text-[10px] font-mono tabular-nums w-10 text-right ${Number(scores.concept_overlap) > 0.3 ? 'text-blue-600' : 'text-slate-400'}`}>
                                        {scores.concept_overlap != null ? scores.concept_overlap.toFixed(3) : '—'}
                                      </span>
                                      <span className={`text-[10px] font-mono tabular-nums w-10 text-right ${Number(scores.scenario_resonance) > 0.3 ? 'text-purple-600' : 'text-slate-400'}`}>
                                        {scores.scenario_resonance != null ? scores.scenario_resonance.toFixed(3) : '—'}
                                      </span>
                                      <span className={`text-[10px] font-mono tabular-nums w-12 text-right font-semibold ${Number(mainScore) > 0.4 ? 'text-emerald-700' : 'text-slate-400'}`}>
                                        {mainScore != null ? Number(mainScore).toFixed(4) : '—'}
                                      </span>
                                    </>
                                  ) : (
                                    <span className={`text-[10px] font-mono tabular-nums ${Number(scores.embedding_cosine) > 0.1 ? 'text-emerald-700' : 'text-slate-400'}`}>
                                      {scores.embedding_cosine != null ? scores.embedding_cosine.toFixed(4) : '—'}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {/* Activated concept chips for selected persona */}
                          {(() => {
                            const selectedEntry = entries.find(([pid]) => pid === pair.persona_id);
                            const mc = selectedEntry?.[1]?.matched_concepts;
                            const scn = selectedEntry?.[1]?.top_scenario_id;
                            if (!mc?.length && !scn) return null;
                            return (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {scn && (
                                  <span className="text-[10px] bg-purple-50 border border-purple-200 text-purple-700 rounded px-1.5 py-0.5">
                                    scn: {scn.replace(/^scn_/, '')}
                                  </span>
                                )}
                                {(mc || []).slice(0, 6).map((c: string) => (
                                  <span key={c} className="text-[10px] bg-blue-50 border border-blue-200 text-blue-700 rounded px-1.5 py-0.5">
                                    {c}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                )}
                {/* Single-score fallback for Pass 2 blended */}
                {!pair.routing_rationale.all_scores && pair.routing_rationale.blended_score != null && (
                  <div className="flex items-center gap-3">
                    {pair.routing_rationale.embedding_cosine != null && (
                      <div><span className="text-slate-400">cos:</span> <span className="font-mono">{pair.routing_rationale.embedding_cosine.toFixed(4)}</span></div>
                    )}
                    {pair.routing_rationale.concept_jaccard != null && (
                      <div><span className="text-slate-400">jaccard:</span> <span className="font-mono">{pair.routing_rationale.concept_jaccard.toFixed(4)}</span></div>
                    )}
                    <div><span className="text-slate-400">blended:</span> <span className="font-mono font-semibold">{pair.routing_rationale.blended_score.toFixed(4)}</span></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attention Atlas + Cortex Decode (Phase 4B) */}
          <AttentionAtlasSection pair={pair} clientUid={clientUid} />

          {/* Inferred Buyer Scenario */}
          {pair.inferred_buyer_scenario && (
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-2">Inferred Buyer Scenario</h3>
              <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2">
                <p className="text-xs text-indigo-800 font-medium">{pair.inferred_buyer_scenario}</p>
                {pair.inferred_buyer_scenario_id && (
                  <p className="text-[10px] text-indigo-400 font-mono mt-1">{pair.inferred_buyer_scenario_id}</p>
                )}
              </div>
            </div>
          )}

          {/* Concepts for this creative */}
          <div>
            <h3 className="text-xs font-semibold text-slate-600 mb-2">
              Concepts ({concepts.length})
            </h3>
            {conceptsQuery.loading && <p className="text-xs text-slate-400">Loading…</p>}
            {concepts.length === 0 && !conceptsQuery.loading && (
              <p className="text-xs text-slate-400">No concepts emitted for this creative.</p>
            )}
            <div className="space-y-2">
              {concepts.map((c) => (
                <div key={c.concept_id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StageBadge stage={c.stage} />
                    <span className="text-xs font-medium text-slate-800">{c.name}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{c.entity_type}</span>
                  </div>
                  {c.resolved_canonical_name && (
                    <div className="text-[10px] text-blue-600 mt-1">→ {c.resolved_canonical_name}</div>
                  )}
                  {c.concept_class && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{c.concept_class}</span>
                      {c.edge_types?.map((e) => (
                        <span key={e} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{e}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C2 — Creatives Tab
// ---------------------------------------------------------------------------
// Run progress header — live "progress through a run" for fan-out runs.
// Reads the GardenRun's run-tree counters (live via the run subscription) +
// the latest execution-log phase. Workflow-agnostic.
// ---------------------------------------------------------------------------

function RunProgressHeader({ run }: { run: GardenRun }) {
  const total = run.tasks_total ?? 0;
  if (total <= 0) return null; // only meaningful for fan-out runs
  const done = run.tasks_done ?? 0;
  const failed = run.tasks_failed ?? 0;
  const running = Math.max(0, total - done - failed);
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const log = run.execution_log;
  const last = log && log.length > 0 ? log[log.length - 1] : undefined;
  const terminal = !RUNNING.has(run.status);
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm font-medium text-slate-700">
          {done}/{total} creatives reviewed
          {failed > 0 && <span className="text-red-600"> · {failed} failed</span>}
          {running > 0 && !terminal && (
            <span className="text-amber-600"> · {running} running</span>
          )}
        </span>
        {last && (
          <span className="text-[11px] text-slate-400 truncate max-w-[55%]" title={last.insight}>
            <span className="uppercase tracking-wider mr-1.5">{last.phase}</span>
            {last.insight}
          </span>
        )}
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="bg-green-500 transition-all" style={{ width: `${pct(done)}%` }} />
        <div className="bg-red-500 transition-all" style={{ width: `${pct(failed)}%` }} />
        {!terminal && running > 0 && (
          <div
            className="bg-amber-400 animate-pulse transition-all"
            style={{ width: `${pct(running)}%` }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks tab — curation-fleet run tree (per-creative + cascade CurationTasks)
// ---------------------------------------------------------------------------

function _taskTone(status: string): string {
  if (status === 'done') return 'bg-green-50 text-green-700';
  if (status === 'failed') return 'bg-red-50 text-red-600';
  if (status === 'running' || status === 'dispatched') return 'bg-amber-50 text-amber-700';
  if (status === 'deferred' || status === 'saturated') return 'bg-slate-100 text-slate-600';
  return 'bg-slate-50 text-slate-500';
}

function TaskRow({ task }: { task: GardenRunTask }) {
  const target =
    (task.target_ref?.creative_id as string | undefined) ??
    (task.target_ref?.concept_name as string | undefined) ??
    JSON.stringify(task.target_ref ?? {});
  const icon =
    task.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
    : task.status === 'failed' ? <XCircle className="w-3.5 h-3.5 text-red-500" />
    : task.status === 'running' || task.status === 'dispatched'
      ? <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" />
      : <Clock className="w-3.5 h-3.5 text-slate-300" />;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 text-sm">
      {icon}
      <span className="flex-1 min-w-0 truncate">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">
          {task.kind}
        </span>
        <span className="font-mono text-slate-800">{String(target)}</span>
        {task.depth > 0 && (
          <span className="ml-2 text-[10px] text-slate-400">depth {task.depth}</span>
        )}
        {task.error && (
          <span className="ml-2 text-[10px] text-red-600 italic truncate">{task.error}</span>
        )}
      </span>
      {task.attempts > 1 && (
        <span className="text-[10px] font-mono text-amber-600">×{task.attempts}</span>
      )}
      <span className="text-xs font-mono text-slate-400">{timeAgo(task.updated_at)}</span>
    </div>
  );
}

function TasksTab({
  clientUid,
  runId,
  isRunning,
}: {
  clientUid: string;
  runId: string;
  isRunning?: boolean;
}) {
  const tasks = useFetch(
    () => gardenApi.getRunTasks(clientUid, runId),
    [clientUid, runId],
  );
  // Live: while the run is active, poll the task tree so statuses update
  // without a manual refresh (the run-level counters are already live via the
  // GardenRun subscription; this keeps the per-task list in step).
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => tasks.refetch(), 4000);
    return () => clearInterval(id);
  }, [isRunning, tasks]);
  const data = tasks.data;
  if (tasks.loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-10 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading tasks…
      </div>
    );
  }
  if (tasks.error) return <ErrorBanner error={tasks.error} onRetry={tasks.refetch} />;
  if (!data || data.tasks.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-10 text-center">
        No per-creative tasks for this run (it didn't fan out into a task tree).
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.counts).map(([status, n]) => (
          <span
            key={status}
            className={clsx('px-2.5 py-1 rounded-full text-xs font-mono', _taskTone(status))}
          >
            {status}: {n}
          </span>
        ))}
      </div>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
        {data.tasks.map((t) => (
          <TaskRow key={t.task_id} task={t} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Pick the "display" pair for a creative group.
 *  Priority: (1) explicit is_primary routing_rationale, (2) highest overall_score, (3) first. */
function pickPrimaryPair(group: ReviewedPair[]): ReviewedPair {
  const explicit = group.find((p) => p.routing_rationale?.is_primary);
  if (explicit) return explicit;
  return group.reduce((best, p) =>
    (p.overall_score ?? -Infinity) > (best.overall_score ?? -Infinity) ? p : best,
    group[0],
  );
}

function CreativesTab({ pairs, pipelines, clientUid, runId }: {
  pairs: ReviewedPair[];
  pipelines: CreativeReviewPipelineState[];
  clientUid: string;
  runId: string;
}) {
  const [viewMode, setViewMode] = useState<'tile' | 'list'>('tile');
  // List view: which creative rows are expanded to show secondary personas
  const [expandedCreatives, setExpandedCreatives] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<ReviewedPair | null>(null);
  const [selectedPipeline, setSelectedPipeline] = useState<CreativeReviewPipelineState | null>(null);

  const pipelineMap = new Map(pipelines.map((p) => [`${p.creative_id}__${p.persona_id}`, p]));

  if (pairs.length === 0) {
    return <p className="text-sm text-slate-500">No reviewed pairs in this run.</p>;
  }

  // Group all pairs by creative_id
  const groupedByCreative = pairs.reduce<Map<string, ReviewedPair[]>>((acc, p) => {
    const existing = acc.get(p.creative_id) ?? [];
    existing.push(p);
    acc.set(p.creative_id, existing);
    return acc;
  }, new Map());

  const toggleExpand = (creativeId: string) => {
    setExpandedCreatives((prev) => {
      const next = new Set(prev);
      if (next.has(creativeId)) next.delete(creativeId);
      else next.add(creativeId);
      return next;
    });
  };

  return (
    <>
      {/* View mode toggle */}
      <div className="flex justify-end mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
          <button
            onClick={() => setViewMode('tile')}
            className={clsx('px-3 py-1.5 text-xs flex items-center gap-1.5', viewMode === 'tile' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50')}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Tiles
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={clsx('px-3 py-1.5 text-xs flex items-center gap-1.5', viewMode === 'list' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50')}
          >
            <List className="w-3.5 h-3.5" /> List
          </button>
        </div>
      </div>

      {/* Tile view — 1 tile per creative, primary persona displayed */}
      {viewMode === 'tile' && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from(groupedByCreative.entries()).map(([creativeId, group]) => {
            const primary = pickPrimaryPair(group);
            const secondaryCount = group.length - 1;
            return (
              <CreativeTile
                key={creativeId}
                pair={primary}
                clientUid={clientUid}
                secondaryCount={secondaryCount}
                onClick={() => setSelected(primary)}
              />
            );
          })}
        </div>
      )}

      {/* List view — 1 row per creative; expand to see secondary personas */}
      {viewMode === 'list' && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-12">Image</th>
                <th className="px-3 py-2 text-left">Creative</th>
                <th className="px-3 py-2 text-left">Assigned Persona</th>
                <th className="px-3 py-2 text-left">Verdict</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2 text-left max-w-[180px]">Strength</th>
                <th className="px-3 py-2 text-left max-w-[180px]">Gap</th>
                <th className="px-3 py-2 text-left w-20">Pipeline</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from(groupedByCreative.entries()).map(([creativeId, group]) => {
                const primary = pickPrimaryPair(group);
                const secondaries = group.filter((p) => p !== primary);
                const isExpanded = expandedCreatives.has(creativeId);
                const key = `${primary.creative_id}__${primary.persona_id}`;
                const pipeline = pipelineMap.get(key);
                const pStatus = pipeline?.status;
                return (
                  <>
                    {/* Primary row */}
                    <tr
                      key={`primary-${creativeId}`}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelected(primary)}
                    >
                      <td className="px-3 py-2">
                        <CreativeThumb creativeId={primary.creative_id} clientUid={clientUid} size="sm" imageUrl={primary.image_url} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs text-slate-700">{primary.creative_name || primary.creative_id.slice(-12)}</div>
                        {secondaries.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(creativeId); }}
                            className="mt-0.5 text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5"
                          >
                            <ChevronRight className={clsx('w-3 h-3 transition-transform', isExpanded && 'rotate-90')} />
                            +{secondaries.length} persona{secondaries.length > 1 ? 's' : ''}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium text-slate-700">
                          {primary.persona_name || primary.persona_id.slice(-8)}
                        </div>
                        {primary.routing_rationale?.is_primary && (
                          <span className="text-[9px] text-teal-600 font-semibold uppercase tracking-wider">primary</span>
                        )}
                      </td>
                      <td className="px-3 py-2"><VerdictChip verdict={primary.verdict} /></td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-700">{primary.overall_score ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-[180px] truncate">{primary.top_strength ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-[180px] truncate">{primary.critical_gap ?? '—'}</td>
                      <td className="px-3 py-2">
                        {pipeline ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedPipeline(pipeline); }}
                            className={clsx(
                              'text-xs px-2 py-0.5 rounded-full border font-medium hover:opacity-80',
                              pStatus === 'completed' ? 'bg-green-50 text-green-700 border-green-200'
                                : pStatus === 'failed' ? 'bg-red-50 text-red-600 border-red-200'
                                  : 'bg-slate-50 text-slate-500 border-slate-200',
                            )}
                          >
                            {pStatus ?? 'view'}
                          </button>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </td>
                    </tr>

                    {/* Expanded secondary persona rows */}
                    {isExpanded && secondaries.map((sec) => {
                      const secKey = `${sec.creative_id}__${sec.persona_id}`;
                      const secPipeline = pipelineMap.get(secKey);
                      const secStatus = secPipeline?.status;
                      return (
                        <tr
                          key={`sec-${sec.creative_id}-${sec.persona_id}`}
                          className="bg-indigo-50/40 hover:bg-indigo-50 cursor-pointer border-l-2 border-l-indigo-200"
                          onClick={() => setSelected(sec)}
                        >
                          <td className="px-3 py-1.5 pl-6" />
                          <td className="px-3 py-1.5 pl-6">
                            <span className="text-[10px] text-slate-400 italic">secondary</span>
                          </td>
                          <td className="px-3 py-1.5 text-xs text-slate-600">
                            {sec.persona_name || sec.persona_id.slice(-8)}
                          </td>
                          <td className="px-3 py-1.5"><VerdictChip verdict={sec.verdict} /></td>
                          <td className="px-3 py-1.5 text-right text-slate-600">{sec.overall_score ?? '—'}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[180px] truncate">{sec.top_strength ?? '—'}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[180px] truncate">{sec.critical_gap ?? '—'}</td>
                          <td className="px-3 py-1.5">
                            {secPipeline ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedPipeline(secPipeline); }}
                                className={clsx(
                                  'text-xs px-2 py-0.5 rounded-full border font-medium hover:opacity-80',
                                  secStatus === 'completed' ? 'bg-green-50 text-green-700 border-green-200'
                                    : secStatus === 'failed' ? 'bg-red-50 text-red-600 border-red-200'
                                      : 'bg-slate-50 text-slate-500 border-slate-200',
                                )}
                              >
                                {secStatus ?? 'view'}
                              </button>
                            ) : <span className="text-xs text-slate-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-creative drill-down panel */}
      {selected && (
        <CreativePairPanel
          pair={selected}
          clientUid={clientUid}
          runId={runId}
          onClose={() => setSelected(null)}
        />
      )}

      {selectedPipeline && (
        <PipelineDetailModal pipeline={selectedPipeline} onClose={() => setSelectedPipeline(null)} />
      )}
    </>
  );
}

/** Fetches and renders a creative thumbnail by looking it up from the ad catalog API. */
function CreativeThumb({ creativeId, clientUid, size = 'md', imageUrl: imageUrlProp }: { creativeId: string; clientUid: string; size?: 'sm' | 'md' | 'lg'; imageUrl?: string | null }) {
  const [url, setUrl] = useState<string | null>(imageUrlProp ?? null);

  useEffect(() => {
    // If the caller already has a resolved HTTPS URL (e.g. from ReviewedPair.image_url)
    // skip the API lookup entirely.
    if (imageUrlProp) {
      setUrl(imageUrlProp);
      return;
    }
    let cancelled = false;
    // Use adCatalogApi (which has the correct VITE_API_BASE_URL prefix) instead
    // of a bare fetch — bare relative paths break when served from the Firebase
    // emulator on :5002 (it returns the SPA HTML rather than proxying to :8100).
    adCatalogApi.getCreative(clientUid, creativeId)
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        const form = (data?.form ?? data) as Record<string, unknown>;
        const resolved =
          (form?.image_url as string) || (form?.image_urls as string[])?.[0] ||
          (form?.thumbnail_url as string) || (form?.thumbnail_gcs_url as string) ||
          (form?.image_gcs_url as string) ||
          (form?.image_gcs_paths as string[])?.[0] ||
          (form?.thumbnail_gcs_path as string) || (form?.video_gcs_url as string) || null;
        setUrl(resolved);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [creativeId, clientUid, imageUrlProp]);

  const dim = size === 'sm' ? 'w-10 h-10' : size === 'lg' ? 'w-full aspect-video' : 'w-full aspect-video';
  if (!url) return <div className={clsx('bg-slate-100 rounded flex items-center justify-center text-slate-300 text-[10px]', dim)}>no img</div>;
  return <img src={url} alt="" className={clsx('object-cover rounded bg-slate-100', size === 'sm' ? 'w-10 h-10' : 'w-full aspect-video')} />;
}

function CreativeTile({ pair, clientUid, onClick, secondaryCount = 0 }: {
  pair: ReviewedPair;
  clientUid: string;
  onClick: () => void;
  secondaryCount?: number;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="w-full aspect-video bg-slate-100 relative">
        <CreativeThumb creativeId={pair.creative_id} clientUid={clientUid} size="lg" imageUrl={pair.image_url} />
        {secondaryCount > 0 && (
          <span className="absolute top-1.5 right-1.5 text-[9px] bg-black/50 text-white px-1.5 py-0.5 rounded-full">
            +{secondaryCount}
          </span>
        )}
      </div>
      <div className="px-3 py-2 space-y-1">
        <div className="flex items-center justify-between gap-1">
          <VerdictChip verdict={pair.verdict} />
          {pair.overall_score != null && (
            <span className="text-xs font-semibold text-slate-700">{pair.overall_score.toFixed(1)}</span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 font-mono truncate">{pair.creative_name || pair.creative_id.slice(-12)}</div>
        {pair.persona_name && (
          <div className="text-[10px] text-teal-600 font-medium truncate">{pair.persona_name}</div>
        )}
        {pair.top_strength && (
          <p className="text-[11px] text-slate-600 line-clamp-2">{pair.top_strength}</p>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// C3 — Reasoning Tab
// ---------------------------------------------------------------------------

function ScoreBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(Math.min(100, Math.max(0, value * 10)));
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-600 mb-0.5">
        <span>{label}</span><span>{value.toFixed(1)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-brand-teal rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ReasoningTab({ pairs }: { pairs: ReviewedPair[] }) {
  const [selected, setSelected] = useState<ReviewedPair | null>(null);
  const reviewQuery = useFetch(
    () => selected?.review_id
      ? gardenApi.getCreativeReview(selected.review_id)
      : Promise.resolve(null),
    [selected?.review_id],
  );

  const review = reviewQuery.data as Record<string, unknown> | null;
  const payload = (review?.payload ?? review ?? {}) as Record<string, unknown>;
  const scores = (payload.scores ?? {}) as Record<string, number>;
  const personaAnalysis = payload.persona_analysis as Record<string, unknown> | undefined;
  const walkthrough = payload.cognitive_walkthrough as Array<Record<string, unknown>> | undefined;
  const recommendations = payload.recommendations as Array<Record<string, unknown>> | undefined;
  const predictive = payload.predictive_impact as Record<string, unknown> | undefined;
  // Support both field name variants
  const briefMods = (payload.ranked_brief_mods ?? payload.ranked_brief_modifications) as Array<Record<string, unknown>> | undefined;
  // Observation pass: verdict=observe + confidence=0 → scores/persona-analysis are
  // sentinel 5.0 placeholders, not real grades. Hide them and show a notice instead.
  const execSummary = payload.executive_summary as Record<string, unknown> | undefined;
  const isObservationPass = execSummary?.verdict === 'observe' || Number(execSummary?.confidence) === 0;

  // Safe string extractor — returns string or empty string for any value
  const safeStr = (v: unknown): string => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
      const obj = v as Record<string, unknown>;
      return String(obj.content_summary ?? obj.summary ?? obj.text ?? obj.value ?? JSON.stringify(v));
    }
    return String(v);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Pair selector */}
      <div className="md:col-span-1 space-y-1 max-h-[500px] overflow-y-auto">
        {pairs.map((p, i) => (
          <button
            key={`${p.creative_id}-${i}`}
            onClick={() => setSelected(p)}
            className={clsx(
              'w-full text-left px-3 py-2 rounded-lg text-xs border transition-colors',
              selected?.review_id === p.review_id ? 'border-brand-teal bg-teal-50' : 'border-slate-200 hover:bg-slate-50',
            )}
          >
            <div className="font-mono text-slate-700 text-xs">{p.creative_name || p.creative_id.slice(-10)}</div>
            <div className="text-slate-400 text-xs">{p.persona_name || p.persona_id.slice(-10)}</div>
            <VerdictChip verdict={p.verdict} />
          </button>
        ))}
      </div>

      {/* Detail panel */}
      <div className="md:col-span-2 space-y-4">
        {!selected && <p className="text-sm text-slate-500">Select a pair to view structured reasoning.</p>}
        {selected && reviewQuery.loading && <p className="text-sm text-slate-500">Loading review…</p>}
        {selected && !reviewQuery.loading && !review && (
          <p className="text-sm text-slate-400">Review not found (may still be processing).</p>
        )}
        {review && (
          <>
            {/* Observation pass notice — shown instead of scores/persona-analysis */}
            {isObservationPass && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
                <span className="text-amber-500 mt-0.5 text-base leading-none">◑</span>
                <div>
                  <p className="text-xs font-semibold text-amber-800">Observation pass (FAST depth)</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    No verdict or scores yet — this creative was catalogued for pattern tracking.
                    Re-run with <code className="bg-amber-100 px-1 rounded">review_depth: full</code> to get a graded analysis with recommendations.
                  </p>
                </div>
              </div>
            )}

            {/* Score grid — hidden for observation passes (sentinel 5.0 values carry no meaning) */}
            {!isObservationPass && Object.keys(scores).length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <h4 className="text-xs font-semibold text-slate-600 mb-3">Score dimensions</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(scores).map(([k, v]) => (
                    <ScoreBar key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'number' ? v : 0} />
                  ))}
                </div>
              </div>
            )}

            {/* Persona analysis — hidden for observation passes (evidence arrays are empty) */}
            {!isObservationPass && personaAnalysis && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Persona analysis</h4>
                {Object.entries(personaAnalysis).map(([key, val]) => {
                  if (val == null) return null;
                  const items = Array.isArray(val) ? val : [val];
                  return (
                    <div key={key} className="mb-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">{key.replace(/_/g, ' ')}</div>
                      <ul className="space-y-0.5">
                        {items.map((item, j) => <li key={j} className="text-xs text-slate-700">• {safeStr(item)}</li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Cognitive walkthrough */}
            {walkthrough && walkthrough.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Cognitive walkthrough</h4>
                <div className="space-y-2">
                  {walkthrough.map((w, i) => {
                    const moment = w.moment as Record<string, unknown> | string | undefined;
                    const momentLabel = safeStr(moment);
                    // Support both {thought} and {persona_reaction} payload shapes
                    const reaction = w.persona_reaction as Record<string, unknown> | undefined;
                    const thoughtText = safeStr(w.thought ?? reaction?.belief_shift ?? reaction?.summary ?? reaction);
                    return (
                      <div key={i} className="rounded-lg bg-slate-50 px-3 py-2">
                        {momentLabel && <div className="text-[10px] text-slate-400 mb-0.5">{momentLabel}</div>}
                        {thoughtText && <p className="text-xs text-slate-700 italic">"{thoughtText}"</p>}
                        {reaction?.intensity != null && (
                          <div className="text-[10px] text-slate-400 mt-0.5">intensity: {Number(reaction.intensity).toFixed(2)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {recommendations && recommendations.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Recommendations</h4>
                <div className="space-y-2">
                  {recommendations.map((r, i) => {
                    const text = safeStr(r.insight ?? r.issue ?? r.summary ?? r.suggestion);
                    const sub = safeStr(r.persona_rationale ?? (r.suggestion as Record<string, unknown>)?.summary);
                    const priority = r.priority as string | undefined;
                    return (
                      <div key={i} className="border-l-2 border-brand-teal pl-3">
                        {priority && <span className="text-[10px] uppercase text-slate-400 font-semibold">{priority} </span>}
                        <p className="text-xs text-slate-800">{text}</p>
                        {sub && text !== sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Predictive impact */}
            {predictive && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Predictive impact</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(predictive).filter(([, v]) => typeof v !== 'object').map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
                      <div className="text-[10px] text-slate-400">{k.replace(/_/g, ' ')}</div>
                      <div className="text-sm font-semibold text-slate-800">{String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ranked brief modifications */}
            {briefMods && briefMods.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Brief modifications</h4>
                <div className="space-y-2">
                  {briefMods.map((m, i) => {
                    const sugText = safeStr(m.suggestion);
                    const lift = m.predicted_lift as number | undefined;
                    return (
                    <div key={i} className="flex items-start gap-2">
                      <span className="shrink-0 text-xs text-slate-400">#{i + 1}</span>
                      <div>
                        <p className="text-xs text-slate-700">{sugText}</p>
                        {lift != null && (
                          <span className="text-[10px] text-green-600">+{(lift * 100).toFixed(0)}% lift</span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C4 — Concepts Tab
// ---------------------------------------------------------------------------

const STAGE_LABELS: Record<string, string> = {
  raw_extracted: 'Raw extracted',
  resolved: 'Resolved (BQ)',
  canonical_pdo: 'Canonical PDO',
};

function ConceptsTab({ artifact, clientUid }: { artifact: CreativeReviewRunArtifact | null; clientUid: string }) {
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [focusCreative, setFocusCreative] = useState<string>('');
  const concepts = artifact?.concepts_emitted ?? [];

  const filtered = concepts.filter((c) => {
    if (stageFilter !== 'all' && c.stage !== stageFilter) return false;
    if (focusCreative && c.creative_id !== focusCreative) return false;
    return true;
  });

  const grouped = filtered.reduce<Record<string, ConceptEmission[]>>((acc, c) => {
    (acc[c.entity_type || 'unknown'] ??= []).push(c);
    return acc;
  }, {});

  const uniqueCreatives = Array.from(new Set(concepts.map((c) => c.creative_id)));

  if (concepts.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <p className="text-sm">No concepts emitted in this run.</p>
        <p className="text-xs mt-1 text-slate-400">Run will populate after creative review agent executes the fanout stage.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
        >
          <option value="all">All stages</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {uniqueCreatives.length > 1 && (
          <select
            value={focusCreative}
            onChange={(e) => setFocusCreative(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
          >
            <option value="">All creatives</option>
            {uniqueCreatives.map((id) => <option key={id} value={id}>{id.slice(-12)}</option>)}
          </select>
        )}
        <span className="text-xs text-slate-400 self-center">{filtered.length} concepts</span>
      </div>

      {Object.entries(grouped).map(([etype, items]) => (
        <div key={etype}>
          <h3 className="text-xs font-semibold text-slate-600 mb-2">{etype} ({items.length})</h3>
          <div className="grid gap-2 md:grid-cols-2">
            {items.map((c) => (
              <div key={`${c.concept_id}-${c.creative_id}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StageBadge stage={c.stage} />
                  <span className="text-xs font-medium text-slate-800">{c.name}</span>
                </div>
                <div className="text-[10px] font-mono text-slate-400">{c.concept_id}</div>
                <div className="text-[10px] text-slate-500">{c.op} · {c.binding_source ?? '—'}</div>
                {c.stage !== 'raw_extracted' && c.resolved_canonical_name && (
                  <div className="text-[10px] text-blue-600">→ Resolved: {c.resolved_canonical_name}</div>
                )}
                {c.concept_class && (
                  <div className="flex gap-1 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{c.concept_class}</span>
                    {c.edge_types?.map((e) => (
                      <span key={e} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{e}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// C5 — Routing Tab
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Footprint Drawer — opens on click of concept_overlap score (ui-3)
// ---------------------------------------------------------------------------

function FootprintDrawer({
  clientUid,
  runId,
  creativeId,
  creativeName,
  onClose,
}: {
  clientUid: string;
  runId: string;
  creativeId: string;
  creativeName?: string | null;
  onClose: () => void;
}) {
  const { data, loading, error } = useFetch<CreativeFootprintResponse>(
    () => gardenApi.getCreativeFootprint(clientUid, runId, creativeId),
    [clientUid, runId, creativeId],
  );

  const methodColor = (m?: string | null) =>
    m === 'cross_cosine' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500';
  const sourceColor = (s?: string | null) =>
    s === 'atlas' ? 'bg-purple-100 text-purple-700 border-purple-200'
    : s === 'extracted_concepts' ? 'bg-teal-100 text-teal-700 border-teal-200'
    : 'bg-amber-100 text-amber-700';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto h-full border-l border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-indigo-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">
              Creative Footprint
            </p>
            <p className="text-[11px] text-slate-400 truncate font-mono">
              {creativeName ?? creativeId.slice(-16)}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        {error && (
          <div className="m-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
            Failed to load footprint: {error}
          </div>
        )}

        {data && (
          <div className="px-4 py-4 space-y-5">
            {/* Scoring method + source badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] px-2 py-1 rounded border font-medium ${methodColor(data.overlap_method)}`}>
                {data.overlap_method === 'cross_cosine' ? '⚡ cross-cosine similarity' : '⊙ jaccard overlap'}
              </span>
              <span className={`text-[11px] px-2 py-1 rounded border font-medium ${sourceColor(data.footprint_source)}`}>
                {data.footprint_source === 'atlas' ? '🗺 atlas footprint'
                  : data.footprint_source === 'extracted_concepts' ? '🔬 extracted concepts'
                  : `⚠ ${data.footprint_source}`}
              </span>
            </div>

            {/* Concept stats */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Total concepts', value: data.total_concepts },
                { label: 'With embeddings', value: data.embedded_count },
                { label: 'Coverage', value: data.total_concepts > 0 ? `${Math.round(data.embedded_count / data.total_concepts * 100)}%` : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-center">
                  <div className="text-lg font-semibold text-slate-900">{value}</div>
                  <div className="text-[10px] text-slate-400">{label}</div>
                </div>
              ))}
            </div>

            {/* Concept activations */}
            {Object.keys(data.concept_activations).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Concept Activations</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {Object.entries(data.concept_activations).slice(0, 20).map(([cid, weight]) => {
                    const hasEmbed = data.embeddings_hydrated.includes(cid);
                    const name = data.concept_names[cid];
                    return (
                      <div key={cid} className="flex items-center gap-2 rounded px-2 py-1 bg-slate-50 border border-slate-100">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${hasEmbed ? 'bg-blue-500' : 'bg-slate-300'}`} />
                        <span className="flex-1 text-[11px] text-slate-700 truncate" title={cid}>
                          {name ?? cid.slice(-20)}
                        </span>
                        <span className="text-[10px] font-mono text-slate-400">{weight.toFixed(2)}</span>
                        {hasEmbed && (
                          <span className="text-[9px] bg-blue-50 text-blue-600 border border-blue-200 rounded px-1">embed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {data.total_concepts > 20 && (
                  <p className="text-[10px] text-slate-400 mt-1">+{data.total_concepts - 20} more</p>
                )}
              </div>
            )}

            {/* Matched concepts for primary persona */}
            {data.matched_concepts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">
                  Matched — {data.primary_persona_name ?? 'Primary'}
                </h4>
                <div className="flex flex-wrap gap-1">
                  {data.matched_concepts.map((mc, i) => (
                    <span key={i} className="text-[10px] bg-indigo-50 border border-indigo-200 text-indigo-700 rounded px-1.5 py-0.5">
                      {mc}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Per-persona score table */}
            {data.persona_summary.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">All Persona Scores</h4>
                <div className="overflow-x-auto rounded-lg border border-slate-100">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] text-slate-400 uppercase">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Persona</th>
                        <th className="px-2 py-1.5 text-right">Cos</th>
                        <th className="px-2 py-1.5 text-right">Overlap</th>
                        <th className="px-2 py-1.5 text-right">Scenario</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-slate-500">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {data.persona_summary.map((ps) => (
                        <tr key={ps.persona_id} className={ps.is_primary ? 'bg-indigo-50' : ''}>
                          <td className={`px-2 py-1.5 font-medium truncate max-w-[130px] ${ps.is_primary ? 'text-indigo-700' : 'text-slate-600'}`}>
                            {ps.is_primary ? `✓ ` : ''}{ps.persona_name ?? ps.persona_id.slice(-10)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-slate-400">
                            {ps.embedding_cosine?.toFixed(3) ?? '—'}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${Number(ps.concept_overlap) > 0.3 ? 'text-blue-600 font-semibold' : 'text-slate-400'}`}>
                            {ps.concept_overlap?.toFixed(3) ?? '—'}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${Number(ps.scenario_resonance) > 0.3 ? 'text-purple-600' : 'text-slate-400'}`}>
                            {ps.scenario_resonance?.toFixed(3) ?? '—'}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono font-semibold ${Number(ps.composite) > 0.4 ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {ps.composite?.toFixed(4) ?? ps.embedding_cosine?.toFixed(4) ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C5 — Routing Tab (enhanced with provider card + heatmap)
// ---------------------------------------------------------------------------

function RoutingTab({
  artifact,
  pairs,
  clientUid,
  runId,
}: {
  artifact: CreativeReviewRunArtifact | null;
  pairs: ReviewedPair[];
  clientUid: string;
  runId: string;
}) {
  const routing = artifact?.persona_routing ?? [];
  const [footprintCreativeId, setFootprintCreativeId] = useState<string | null>(null);
  const { data: health } = useFetch<RoutingHealthResponse>(
    () => gardenApi.getRunRoutingHealth(clientUid, runId),
    [clientUid, runId],
  );

  // Build maps
  const pairRationaleMap = new Map<string, ReviewedPair['routing_rationale']>();
  for (const p of pairs) {
    if (p.routing_rationale && !pairRationaleMap.has(p.creative_id)) {
      pairRationaleMap.set(p.creative_id, p.routing_rationale);
    }
  }
  const creativeNameMap = new Map<string, string>();
  for (const p of pairs) {
    if (!creativeNameMap.has(p.creative_id) && p.creative_name) {
      creativeNameMap.set(p.creative_id, p.creative_name);
    }
  }

  if (routing.length === 0) {
    return <p className="text-sm text-slate-500">No routing decisions recorded.</p>;
  }

  const provider = health?.provider;
  const stats = health?.stats;
  const personaWeights = health?.persona_weights ?? {};
  const personaIds = Array.from(
    new Set(Object.values(personaWeights).flatMap((pw) => Object.keys(pw)))
  ).slice(0, 6);

  return (
    <div className="space-y-4">
      {/* Provider card + scoring health */}
      <div className="grid gap-3 md:grid-cols-2">
        {/* Provider card */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Network className="w-3.5 h-3.5 text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700">Ontology Provider</h3>
          </div>
          {provider ? (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  provider.type === 'pdo_bundle'
                    ? 'bg-emerald-100 text-emerald-700'
                    : provider.type === 'bq_shim'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-slate-100 text-slate-500'
                }`}>
                  {provider.type === 'pdo_bundle' ? '🗺 PDO Bundle' : provider.type === 'bq_shim' ? '⚡ BQ Shim' : 'None'}
                </span>
                {provider.loaded && (
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                )}
              </div>
              {provider.bundle_version && (
                <div className="text-slate-400 font-mono text-[10px]">v{provider.bundle_version}</div>
              )}
              {provider.loaded_clients && provider.loaded_clients.length > 0 && (
                <div className="text-slate-400">Loaded: {provider.loaded_clients.join(', ')}</div>
              )}
              {provider.bq_provider && (
                <div className="text-slate-400">BQ: {provider.bq_provider}</div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Loading provider info…</p>
          )}
        </div>

        {/* Scoring health stats */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <h3 className="text-xs font-semibold text-slate-700 mb-2">Scoring Health</h3>
          {stats ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {stats.mean_composite != null && (
                  <>
                    <span className="text-slate-400">Mean composite</span>
                    <span className="font-mono font-semibold text-emerald-700">{stats.mean_composite.toFixed(4)}</span>
                    <span className="text-slate-400">Mean concept overlap</span>
                    <span className="font-mono text-blue-600">{stats.mean_concept_overlap?.toFixed(4) ?? '—'}</span>
                    <span className="text-slate-400">Mean scenario</span>
                    <span className="font-mono text-purple-600">{stats.mean_scenario_resonance?.toFixed(4) ?? '—'}</span>
                  </>
                )}
              </div>
              {/* Overlap method distribution */}
              {Object.keys(stats.overlap_method).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(stats.overlap_method).map(([method, count]) => (
                    <span key={method} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                      method === 'cross_cosine' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-slate-50 text-slate-500 border-slate-200'
                    }`}>
                      {method}: {count}
                    </span>
                  ))}
                </div>
              )}
              {/* Footprint source distribution */}
              {Object.keys(stats.footprint_source).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(stats.footprint_source).map(([src, count]) => (
                    <span key={src} className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      src === 'atlas' ? 'bg-purple-50 text-purple-600 border-purple-200'
                        : src === 'extracted_concepts' ? 'bg-teal-50 text-teal-600 border-teal-200'
                        : 'bg-amber-50 text-amber-600 border-amber-200'
                    }`}>
                      {src}: {count}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Loading stats…</p>
          )}
        </div>
      </div>

      {/* Concept × Persona heatmap */}
      {Object.keys(personaWeights).length > 0 && personaIds.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
            <h3 className="text-xs font-semibold text-slate-700">Top Concept × Persona Weight Heatmap</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-3 py-1.5 text-left text-[10px] text-slate-400 font-medium uppercase">Concept</th>
                  {personaIds.map((pid) => {
                    const pName = pairs.find((p) => p.persona_id === pid)?.persona_name ?? pid.slice(-10);
                    return (
                      <th key={pid} className="px-2 py-1.5 text-center text-[10px] text-slate-400 font-medium uppercase max-w-[80px] truncate" title={pName}>
                        {pName.split(' ')[0]}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {Object.entries(personaWeights).map(([concept, weights]) => (
                  <tr key={concept} className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-[11px] text-slate-600 max-w-[160px] truncate" title={concept}>
                      {concept}
                    </td>
                    {personaIds.map((pid) => {
                      const w = weights[pid];
                      const pct = w != null ? Math.round(w * 100) : 0;
                      return (
                        <td key={pid} className="px-2 py-1.5 text-center">
                          {w != null ? (
                            <div
                              className="inline-block w-8 h-4 rounded text-[9px] font-mono leading-4 text-center"
                              style={{ backgroundColor: `rgba(99,102,241,${Math.min(w, 1) * 0.8 + 0.1})`, color: pct > 40 ? '#fff' : '#4338ca' }}
                            >
                              {pct}%
                            </div>
                          ) : (
                            <span className="text-slate-200 text-[10px]">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
        <strong>How routing works:</strong> Pass 1 uses cosine similarity between the creative's embedded text and each
        persona's prototype embedding. Pass 2 uses composite scoring: concept overlap (Jaccard or cross-cosine embeddings)
        + scenario resonance + prototype cosine. Click <strong>Footprint</strong> on any creative to inspect concept
        activations and embedding hydration status.
      </div>

      {routing.map((r) => {
        const rationale = pairRationaleMap.get(r.creative_id);
        const creativeName = creativeNameMap.get(r.creative_id);
        const primaryPair = pairs.find((p) => p.creative_id === r.creative_id && p.persona_id === r.primary);
        const primaryName = primaryPair?.persona_name ?? r.primary?.slice(-12) ?? '—';

        // Derive overlap method from the pair's all_scores for the primary persona
        const primaryScores = rationale?.all_scores?.[r.primary ?? ''];
        const om = primaryScores?.overlap_method;
        const fs = primaryScores?.footprint_source;

        return (
          <div key={r.creative_id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-slate-700">{r.creative_id.slice(-12)}</span>
              {creativeName && <span className="text-xs text-slate-600 font-medium truncate max-w-xs">{creativeName}</span>}
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">
                Assigned: <span className="font-semibold text-slate-800">{primaryName}</span>
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                {om && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${
                    om === 'cross_cosine' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-slate-100 text-slate-400 border-slate-200'
                  }`}>
                    {om}
                  </span>
                )}
                {fs && fs !== 'unknown' && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${
                    fs === 'atlas' ? 'bg-purple-50 text-purple-600 border-purple-200'
                      : 'bg-teal-50 text-teal-600 border-teal-200'
                  }`}>
                    {fs === 'atlas' ? 'atlas' : 'concepts'}
                  </span>
                )}
                {rationale?.creative_text_empty && (
                  <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">⚠ empty text</span>
                )}
                <button
                  onClick={() => setFootprintCreativeId(r.creative_id)}
                  className="text-[10px] flex items-center gap-0.5 px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                >
                  <Fingerprint className="w-2.5 h-2.5" />
                  Footprint
                </button>
              </div>
            </div>

            {rationale?.creative_text_preview && (
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60">
                <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Embedded text:</span>
                <p className="text-xs text-slate-600 mt-0.5 italic">{rationale.creative_text_preview}</p>
              </div>
            )}

            {r.candidate_personas.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] text-slate-400 uppercase">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Persona</th>
                      <th className="px-3 py-1.5 text-left">Pass</th>
                      <th className="px-3 py-1.5 text-right">Cosine</th>
                      <th className="px-3 py-1.5 text-right">Jaccard</th>
                      <th className="px-3 py-1.5 text-right">Blended</th>
                      <th className="px-3 py-1.5 text-right">Threshold</th>
                      <th className="px-3 py-1.5 text-center">Selected</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {r.candidate_personas.map((c: PersonaCandidateScore, i: number) => (
                      <tr key={i} className={clsx(c.selected && 'bg-teal-50')}>
                        <td className="px-3 py-1.5">
                          <span className={clsx('font-medium', c.selected ? 'text-teal-800' : 'text-slate-600')}>
                            {c.persona_name ?? c.persona_id.slice(-10)}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-slate-400 text-[10px]">{c.pass_name}</td>
                        <td className={clsx('px-3 py-1.5 text-right font-mono', Number(c.embedding_cosine) > 0.1 ? 'text-emerald-700 font-semibold' : 'text-slate-400')}>
                          {c.embedding_cosine?.toFixed(4) ?? '—'}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-500">{c.concept_jaccard?.toFixed(4) ?? '—'}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-semibold">{c.blended_score?.toFixed(4) ?? '—'}</td>
                        <td className="px-3 py-1.5 text-right text-slate-400">{c.threshold ?? '—'}</td>
                        <td className="px-3 py-1.5 text-center">
                          {c.selected
                            ? <CheckCircle2 className="w-3 h-3 text-green-500 mx-auto" />
                            : <XCircle className="w-3 h-3 text-slate-200 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Footprint drawer */}
      {footprintCreativeId && (
        <FootprintDrawer
          clientUid={clientUid}
          runId={runId}
          creativeId={footprintCreativeId}
          creativeName={creativeNameMap.get(footprintCreativeId)}
          onClose={() => setFootprintCreativeId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// C6 — Strategy Tab
// ---------------------------------------------------------------------------

function StrategyTab({ pairs, clientUid, runId }: { pairs: ReviewedPair[]; clientUid: string; runId: string }) {
  const [synthLoading, setSynthLoading] = useState(false);

  const creativeIds = Array.from(new Set(pairs.map((p) => p.creative_id)));

  const triggerSynthesize = async () => {
    setSynthLoading(true);
    try {
      await fetch(`/api/v1/garden/${encodeURIComponent(clientUid)}/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          run_type: 'STRATEGIC_REBUILD',
          trigger_source: 'manual',
          trigger_id: 'strategy-tab-cta',
          stage_config_overrides: {
            creative_subset_ids: creativeIds,
            stages: ['semantic_clustering', 'brief_synthesis'],
          },
        }),
      });
      alert('Strategy synthesis run triggered. Check the Runs tab for progress.');
    } catch (e) {
      alert(`Failed to trigger: ${String(e)}`);
    } finally {
      setSynthLoading(false);
    }
  };

  if (pairs.length === 0) {
    return <p className="text-sm text-slate-500">No creative pairs in this run.</p>;
  }

  return (
    <div className="space-y-5">
      {/* Explanation */}
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">What is Strategy?</h3>
        <p className="text-sm text-slate-600">
          Strategy in NeuroGraph represents the <em>inferred strategy substrate</em> — the underlying creative logic
          that the brand's creative team used to build each ad. The Creative Review agent extracts ontology bindings
          (concept activations, hook types, angles, emotional levers) from each creative and attempts to map them to
          existing <strong>StrategyInstance</strong> clusters, or — if no existing cluster matches — back-engineers
          what the creative strategy was from first principles.
        </p>
        <p className="text-sm text-slate-600 mt-2">
          This tab shows the strategy snapshot for each reviewed creative in this run.
          Once sufficient reviews have been collected, use the <strong>Synthesize strategy</strong> button below
          to run semantic clustering + brief synthesis on exactly these creatives.
        </p>
      </div>

      {/* Per-creative strategy cards */}
      <div className="grid gap-3 md:grid-cols-2">
        {Array.from(new Set(pairs.map((p) => p.creative_id))).map((creativeId) => {
          const pair = pairs.find((p) => p.creative_id === creativeId)!;
          return (
            <StrategyCard key={creativeId} pair={pair} clientUid={clientUid} />
          );
        })}
      </div>

      {/* Synthesize CTA */}
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center">
        <p className="text-sm text-slate-600 mb-3">
          Run semantic clustering + brief synthesis on the {creativeIds.length} creative{creativeIds.length !== 1 ? 's' : ''} reviewed in this run.
        </p>
        <button
          onClick={triggerSynthesize}
          disabled={synthLoading}
          className="text-sm px-4 py-2 rounded-lg bg-brand-teal text-white hover:opacity-90 disabled:opacity-50"
        >
          {synthLoading ? 'Triggering…' : 'Synthesize strategy from these reviews'}
        </button>
      </div>
    </div>
  );
}

function StrategyCard({ pair, clientUid }: { pair: ReviewedPair; clientUid: string }) {
  const reviewQuery = useFetch(
    () => pair.review_id
      ? fetch(`/api/v1/creative-reviews/${pair.review_id}`).then((r) => r.json())
      : Promise.resolve(null),
    [pair.review_id],
  );
  const review = reviewQuery.data as Record<string, unknown> | null;
  const payload = (review?.payload ?? review ?? {}) as Record<string, unknown>;
  const ontologyBindings = (payload.ontology_bindings ?? []) as Array<{ concept_name?: string; concept_type?: string; activation_weight?: number }>;
  const meaning = (payload.meaning ?? {}) as Record<string, unknown>;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden shrink-0">
          <CreativeThumb creativeId={pair.creative_id} clientUid={clientUid} size="sm" imageUrl={pair.image_url} />
        </div>
        <div>
          <div className="font-mono text-xs text-slate-700">{pair.creative_name || pair.creative_id.slice(-12)}</div>
          <VerdictChip verdict={pair.verdict} />
        </div>
      </div>

      {reviewQuery.loading && <p className="text-xs text-slate-400">Loading review…</p>}

      {/* Hook / angle / emotion from meaning */}
      {Boolean(meaning.hook_type || meaning.angle_archetype || meaning.primary_emotion) && (
        <div className="flex gap-1 flex-wrap">
          {Boolean(meaning.hook_type) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{String(meaning.hook_type)}</span>
          )}
          {Boolean(meaning.angle_archetype) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{String(meaning.angle_archetype)}</span>
          )}
          {Boolean(meaning.primary_emotion) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{String(meaning.primary_emotion)}</span>
          )}
        </div>
      )}

      {/* Ontology bindings — driving PDO factors */}
      {ontologyBindings.length > 0 && (
        <div>
          <div className="text-[10px] text-slate-400 font-semibold mb-1 uppercase">Key PDO concepts</div>
          <div className="flex gap-1 flex-wrap">
            {ontologyBindings.slice(0, 6).map((b, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                {b.concept_name ?? b.concept_type}
              </span>
            ))}
          </div>
        </div>
      )}

      {!review && !reviewQuery.loading && (
        <p className="text-[11px] text-slate-400 italic">Review not yet available — run creative review to extract strategy.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Errors Tab (unchanged logic, improved styling)
// ---------------------------------------------------------------------------

function ErrorsTab({ artifact }: { artifact: CreativeReviewRunArtifact | null }) {
  const errors = artifact?.errors ?? [];
  if (errors.length === 0) return <p className="text-sm text-slate-500">No errors recorded.</p>;

  const grouped = errors.reduce<Record<string, typeof errors>>((acc, e) => {
    (acc[e.op] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([op, items]) => (
        <div key={op}>
          <h3 className="text-xs font-semibold text-slate-600 mb-2">{op} ({items.length})</h3>
          <div className="space-y-2">
            {items.map((e, i) => (
              <div key={i} className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs">
                <span className="font-mono text-slate-700">{e.creative_id}</span>
                {e.persona_id && <span className="text-slate-500"> · {e.persona_id.slice(-10)}</span>}
                <div className="text-red-800 mt-1">{e.error}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function RunDetailView() {
  const { clientUid = '', runId = '' } = useParams<{ clientUid: string; runId: string }>();
  const [tab, setTab] = useState<Tab>('overview');

  const { run, error: runError, loading: runLoading } = useGardenRunLive(clientUid, runId);
  const isRunning = RUNNING.has(run?.status ?? '');
  const pipelines = useGardenRunPipelinesLive(clientUid, runId, isRunning);

  const artifactQuery = useFetch(
    () => gardenApi.getRunArtifact(clientUid, runId).catch(() => null),
    [clientUid, runId],
  );
  const artifact = artifactQuery.data;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link
        to={`/admin/creative-review/${clientUid}/runs`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to runs
      </Link>

      {runError && <ErrorBanner error={runError} onRetry={() => {}} />}
      {runLoading && !run && <p className="text-sm text-slate-500">Loading run…</p>}

      {run && (
        <>
          <header className="mb-4 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-slate-900 font-mono truncate">{run.run_id}</h1>
              <p className="text-sm text-slate-500 mt-1">
                {run.run_type} · {clientUid}
                {run.started_at && <> · {timeAgo(run.started_at)}</>}
              </p>
            </div>
            <RunStatusBadge status={run.status} />
          </header>

          <PipelineOverviewStrip pipelines={pipelines} isRunning={isRunning} />

          <nav className="flex gap-0 border-b border-slate-200 mb-6 overflow-x-auto">
            {([
              ['overview', 'Overview', undefined],
              ['creatives', 'Creatives', artifact?.pairs ? new Set(artifact.pairs.map((p: ReviewedPair) => p.creative_id)).size : undefined],
              ['tasks', 'Tasks', run.tasks_total || undefined],
              ['reasoning', 'Reasoning', undefined],
              ['concepts', 'Concepts', artifact?.concepts_emitted?.length],
              ['routing', 'Routing', artifact?.persona_routing?.length],
              ['strategy', 'Strategy', undefined],
              ['errors', 'Errors', artifact?.errors?.length],
              ['execution_log', 'Log', run.execution_log?.length],
            ] as [Tab, string, number | undefined][]).map(([id, label, count]) => (
              <TabButton
                key={id}
                active={tab === id}
                label={label}
                count={count}
                onClick={() => setTab(id)}
              />
            ))}
          </nav>

          {tab === 'overview' && (
            <OverviewTab run={run} artifact={artifact} clientUid={clientUid} />
          )}
          {tab === 'creatives' && (
            <CreativesTab
              pairs={artifact?.pairs ?? []}
              pipelines={pipelines}
              clientUid={clientUid}
              runId={runId}
            />
          )}
          {tab === 'tasks' && (
            <TasksTab clientUid={clientUid} runId={runId} isRunning={isRunning} />
          )}
          {tab === 'reasoning' && <ReasoningTab pairs={artifact?.pairs ?? []} />}
          {tab === 'concepts' && <ConceptsTab artifact={artifact} clientUid={clientUid} />}
          {tab === 'routing' && (
            <RoutingTab
              artifact={artifact}
              pairs={artifact?.pairs ?? []}
              clientUid={clientUid}
              runId={runId}
            />
          )}
          {tab === 'strategy' && (
            <StrategyTab pairs={artifact?.pairs ?? []} clientUid={clientUid} runId={runId} />
          )}
          {tab === 'errors' && <ErrorsTab artifact={artifact} />}
          {tab === 'execution_log' && <ExecutionLogTab run={run} />}
        </>
      )}
    </div>
  );
}
